/**
 * Hero Metrics Component
 *
 * Displays prominent "hero" metric cards at the top of results tabs.
 * Features gradient backgrounds, large typography, and color-coded categories.
 */

import { Paper, Typography, Grid } from '@mui/material';

export interface HeroMetric {
  label: string;
  value: string;
  subtitle?: string;
  color: string;
}

export interface HeroMetricsProps {
  primary: HeroMetric;
  secondary: HeroMetric[];
}

/**
 * HeroMetrics - Displays key metrics in prominent cards
 *
 * @param props - Contains primary (large) metric and secondary (smaller) metrics
 */
export function HeroMetrics({ primary, secondary }: HeroMetricsProps) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {/* Primary Hero Metric - Large, gradient background */}
      <Grid item xs={12} md={6} lg={5}>
        <Paper
          sx={{
            p: 1.5,
            background: `linear-gradient(135deg, ${primary.color} 0%, ${primary.color}dd 100%)`,
            color: 'white',
            height: '100%',
            minHeight: 90,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 4,
              transition: 'transform 0.2s, box-shadow 0.2s'
            }
          }}
        >
          <Typography variant="overline" sx={{ opacity: 0.9, textTransform: 'uppercase', letterSpacing: 1 }}>
            {primary.label}
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 'bold', my: 1 }}>
            {primary.value}
          </Typography>
          {primary.subtitle && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {primary.subtitle}
            </Typography>
          )}
        </Paper>
      </Grid>

      {/* Secondary Metrics - Smaller cards */}
      {secondary.map((metric, idx) => (
        <Grid item xs={6} md={3} lg={7 / secondary.length} key={idx}>
          <Paper
            sx={{
              p: 1,
              height: '100%',
              minHeight: 70,
              bgcolor: metric.color,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 2,
                transition: 'transform 0.2s, box-shadow 0.2s'
              }
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {metric.label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 0.5 }}>
              {metric.value}
            </Typography>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}
